// ============================================================================
// AI Search — direct AppSync client
//
// The AutoMate agent feature uses its own dedicated AppSync API with Cognito
// user-pool auth. Rather than routing calls through the shared `Amplify` /
// `Amplify.configure()` mechanism, this client talks to that API directly:
//
//   - Mutations go over plain `fetch()` straight to the AI AppSync endpoint
//   - Subscriptions use a native WebSocket that speaks AppSync's real-time
//     protocol directly (Cognito user pool auth, graphql-ws subprotocol)
//
// Why direct instead of Amplify:
//   aws-amplify v5 only supports a single global AppSync config at a time, and
//   other parts of this app swap that config frequently (Plus / Generic /
//   Flexico / MJE). A long-lived agent subscription started via Amplify will
//   reliably be interrupted by one of those swaps and end up pointed at the
//   wrong endpoint. Talking to the AI AppSync directly makes the agent
//   pipeline immune to global-config changes elsewhere in the app.
//
// Both calls pull the Cognito ID token from Amplify's `Auth` module so auth
// still works — we only bypass the transport / config layer, not auth.
//
// Protocol reference:
//   https://docs.aws.amazon.com/appsync/latest/devguide/real-time-websocket-client.html
// ============================================================================

import { Auth } from 'aws-amplify';
import { AGENT_GRAPHQL_CONFIG } from 'src/constants/AppConstants';
import { AI_AGENT_DEBUG_LOGS } from 'src/Ateam-BI/AutoMate/constants';

const LOG_PREFIX = '[AISearchAppSync]';

/**
 * Emit a verbose log only when the `AI_AGENT_DEBUG_LOGS` flag is on.
 *
 * These logs include endpoint URLs, WebSocket payloads, and streamed AI
 * response chunks — i.e. data we don't want leaking into the production
 * console. Flip the flag in `src/Ateam-BI/AutoMate/constants.js` when you
 * need them.
 *
 * Errors and unexpected transport states still go through `console.error` /
 * `console.warn` unconditionally.
 */
const debugLog = (...args) => {
  if (AI_AGENT_DEBUG_LOGS) console.log(LOG_PREFIX, ...args);
};

const getEndpoint = () => {
  const Stage = 'beta';
  const entry = AGENT_GRAPHQL_CONFIG.find((c) => c.environment === Stage);
  if (!entry) throw new Error(`No agent AppSync endpoint configured for stage '${Stage}'`);
  return entry.grapQLEndPoint;
};

const getIdToken = async () => {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
};

// 'abc.appsync-api.us-east-1.amazonaws.com' → 'abc.appsync-realtime-api.us-east-1.amazonaws.com'
const getRealtimeHost = (httpUrl) => new URL(httpUrl).host.replace('appsync-api', 'appsync-realtime-api');
const getHttpHost = (httpUrl) => new URL(httpUrl).host;

// Standard base64 (WITH `=` padding). AppSync expects the full base64 string,
// not the base64url variant.
const b64 = (obj) => btoa(JSON.stringify(obj));

/**
 * Fire a GraphQL mutation against the agent AppSync endpoint.
 * Returns the parsed JSON response: `{ data, errors? }`.
 *
 * @param {string} query
 * @param {object} variables
 * @param {{ signal?: AbortSignal }} [options] – optional options bag. When a
 *      `signal` is provided, it's forwarded to the underlying `fetch()` so
 *      callers (e.g. `useAgentSuggestions`) can cancel the in-flight request.
 *      Aborting surfaces as a `DOMException` whose `name === 'AbortError'`,
 *      which callers can use to distinguish cancellation from real errors.
 */
export const aiGraphqlMutation = async (query, variables, options = {}) => {
  const { signal } = options;
  const endpoint = getEndpoint();
  const token = await getIdToken();
  debugLog('mutation POST →', endpoint);
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ query, variables }),
    ...(signal && { signal }),
  });
  return response.json();
};

/**
 * Open a GraphQL subscription over AppSync's real-time WebSocket protocol.
 * Returns an object with `{ unsubscribe }`.
 *
 * @param {string} query      – subscription document
 * @param {object} variables  – subscription variables
 * @param {(payload: any) => void} onData  – fired for each `data` message. The
 *     payload has the shape `{ data: { onAgentResponse: {...} } }`.
 * @param {(err: any) => void} [onError]   – fired on protocol or transport error
 */
export const aiGraphqlSubscribe = async (query, variables, onData, onError) => {
  const endpoint = getEndpoint();
  const token = await getIdToken();
  const httpHost = getHttpHost(endpoint);
  const realtimeHost = getRealtimeHost(endpoint);

  // Handshake header (used in the ?header=... query param for the WebSocket URL).
  const handshakeHeader = { Authorization: token, host: httpHost };
  const headerParam = encodeURIComponent(b64(handshakeHeader));
  const payloadParam = encodeURIComponent(b64({}));
  const wsUrl = `wss://${realtimeHost}/graphql?header=${headerParam}&payload=${payloadParam}`;

  const ws = new WebSocket(wsUrl, 'graphql-ws');
  const subscriptionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  let isClosed = false;
  let keepAliveTimer = null;
  let connectionTimeoutMs = 5 * 60 * 1000; // default 5 min per AppSync docs

  const resetKeepAlive = () => {
    if (keepAliveTimer) clearTimeout(keepAliveTimer);
    keepAliveTimer = setTimeout(() => {
      console.warn(`${LOG_PREFIX} keep-alive timeout — closing WebSocket`);
      try { ws.close(); } catch (_) { /* noop */ }
    }, connectionTimeoutMs);
  };

  const safeError = (err) => {
    if (onError) {
      try { onError(err); } catch (_) { /* swallow */ }
    }
  };

  ws.onopen = () => {
    debugLog('ws open, sending connection_init');
    ws.send(JSON.stringify({ type: 'connection_init' }));
  };

  ws.onmessage = (event) => {
    let msg;
    try { msg = JSON.parse(event.data); } catch { return; }
    debugLog('ws msg', msg.type, msg);

    switch (msg.type) {
      case 'connection_ack': {
        if (msg.payload && typeof msg.payload.connectionTimeoutMs === 'number') {
          connectionTimeoutMs = msg.payload.connectionTimeoutMs;
        }
        resetKeepAlive();
        debugLog('connection_ack, registering subscription', { subscriptionId });
        const startMessage = {
          id: subscriptionId,
          type: 'start',
          payload: {
            data: JSON.stringify({ query, variables }),
            extensions: {
              authorization: {
                Authorization: token,
                host: httpHost,
              },
            },
          },
        };
        ws.send(JSON.stringify(startMessage));
        break;
      }
      case 'ka':
        resetKeepAlive();
        break;
      case 'start_ack':
        debugLog('start_ack', { id: msg.id });
        break;
      case 'data':
        if (msg.id === subscriptionId && msg.payload) {
          debugLog('chunk received', msg.payload?.data?.onAgentResponse ?? msg.payload);
          try { onData(msg.payload); } catch (_) { /* swallow */ }
        }
        break;
      case 'error':
        console.error(`${LOG_PREFIX} error message from server:`, msg.payload);
        safeError(msg.payload || msg);
        break;
      case 'complete':
        if (msg.id === subscriptionId) {
          isClosed = true;
          if (keepAliveTimer) clearTimeout(keepAliveTimer);
          try { ws.close(); } catch (_) { /* noop */ }
        }
        break;
      default:
        // Ignore unknown message types
        break;
    }
  };

  ws.onerror = (event) => {
    console.error(`${LOG_PREFIX} ws error event`, event);
    safeError(event);
  };

  ws.onclose = (event) => {
    if (keepAliveTimer) clearTimeout(keepAliveTimer);
    if (!isClosed) {
      // Unexpected close — surface to the consumer so it can clear loading
      // state, show an error, etc. Without this the consumer has no way to
      // know the connection died (only a silent console.warn would fire)
      // and can get stuck in a permanent "streaming" state.
      console.warn(`${LOG_PREFIX} ws closed unexpectedly`, { code: event.code, reason: event.reason });
      isClosed = true;
      safeError({ type: 'close', code: event.code, reason: event.reason });
    }
  };

  return {
    unsubscribe: () => {
      if (isClosed) return;
      isClosed = true;
      if (keepAliveTimer) clearTimeout(keepAliveTimer);
      try {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ id: subscriptionId, type: 'stop' }));
        }
      } catch (_) { /* noop */ }
      try { ws.close(); } catch (_) { /* noop */ }
    },
  };
};
